import { OccupancyLease } from './types'
import {
    genderDisplayLabel,
    normalizeGenderValue,
} from '../../ApplicationWorkbook/genderQuestion'

export type LifestyleFieldId =
    | 'study_habits'
    | 'sleep_schedule'
    | 'cleanliness'
    | 'noise_tolerance'
    | 'social_preference'

export type LifestyleImportance = 'must' | 'prefer' | 'none'

const MATCH_FIELDS: Record<
    LifestyleFieldId,
    { geometry: 'similar' | 'asymmetric'; options: string[] }
> = {
    study_habits: {
        geometry: 'similar',
        options: ['Quiet / focused', 'Mixed', 'Social study'],
    },
    sleep_schedule: {
        geometry: 'similar',
        options: ['Early sleeper', 'Average', 'Night owl'],
    },
    cleanliness: {
        geometry: 'asymmetric',
        options: ['Very tidy', 'Average', 'Relaxed'],
    },
    noise_tolerance: {
        geometry: 'asymmetric',
        options: ['Prefers quiet', 'Average', 'Tolerates noise'],
    },
    social_preference: {
        geometry: 'similar',
        options: ['Mostly private', 'Balanced', 'Very social'],
    },
}

const SHORT_VALUES: Record<string, string> = {
    'Quiet / focused': 'Quiet',
    Mixed: 'Mixed',
    'Social study': 'Social',
    'Early sleeper': 'Early',
    Average: 'Average',
    'Night owl': 'Night',
    'Very tidy': 'Tidy',
    Relaxed: 'Relaxed',
    'Prefers quiet': 'Quiet',
    'Tolerates noise': 'Loud OK',
    'Mostly private': 'Private',
    Balanced: 'Balanced',
    'Very social': 'Social',
}

export const LIFESTYLE_COLUMNS: { id: LifestyleFieldId; label: string }[] = [
    { id: 'sleep_schedule', label: 'Sleep' },
    { id: 'cleanliness', label: 'Clean' },
    { id: 'noise_tolerance', label: 'Noise' },
    { id: 'social_preference', label: 'Social' },
    { id: 'study_habits', label: 'Study' },
]

export const PM_WEIGHT: Record<string, number> = {
    high: 3,
    medium: 2,
    low: 1,
}

export const APP_WEIGHT: Record<LifestyleImportance, number> = {
    must: 3,
    prefer: 2,
    none: 0.5,
}

export const DEFAULT_PM_WEIGHTS: Record<
    LifestyleFieldId,
    'high' | 'medium' | 'low'
> = {
    study_habits: 'medium',
    sleep_schedule: 'high',
    cleanliness: 'high',
    noise_tolerance: 'high',
    social_preference: 'medium',
}

function optionIndex(
    fieldId: LifestyleFieldId,
    value: string | null | undefined,
) {
    if (value == null || value === '') return null
    const index = MATCH_FIELDS[fieldId].options.indexOf(value)
    return index < 0 ? null : index
}

export function dimensionScore(
    fieldId: LifestyleFieldId,
    aIdx: number | null,
    bIdx: number | null,
) {
    if (aIdx == null || bIdx == null) return 0
    const geometry = MATCH_FIELDS[fieldId].geometry
    if (geometry === 'similar') {
        if (aIdx === 1 || bIdx === 1) return 1
        return aIdx === bIdx ? 1 : 0
    }
    const gap = Math.abs(aIdx - bIdx)
    if (gap === 0) return 1
    return 1 - gap / 2
}

export function pairLifestyleScore(
    left: OccupancyLease,
    right: OccupancyLease,
): {
    score: number
    hardFail: boolean
    dims: Record<LifestyleFieldId, number>
} {
    let total = 0
    let weightSum = 0
    const dims = {} as Record<LifestyleFieldId, number>
    for (const fieldId of Object.keys(MATCH_FIELDS) as LifestyleFieldId[]) {
        const dim = dimensionScore(
            fieldId,
            optionIndex(fieldId, left.lifestyle_answers?.[fieldId]),
            optionIndex(fieldId, right.lifestyle_answers?.[fieldId]),
        )
        dims[fieldId] = dim
        const aImp = lifestyleImportance(left, fieldId)
        const bImp = lifestyleImportance(right, fieldId)
        if (dim === 0 && (aImp === 'must' || bImp === 'must')) {
            return { score: 0, hardFail: true, dims }
        }
        const weight =
            (PM_WEIGHT[DEFAULT_PM_WEIGHTS[fieldId]] ?? 2) *
            ((APP_WEIGHT[aImp] + APP_WEIGHT[bImp]) / 2)
        total += dim * weight
        weightSum += weight
    }
    return {
        score: weightSum <= 0 ? 0 : total / weightSum,
        hardFail: false,
        dims,
    }
}

export function lifestyleImportance(
    lease: OccupancyLease,
    fieldId: LifestyleFieldId,
): LifestyleImportance {
    const raw = String(
        lease.lifestyle_answers?.[`${fieldId}__importance`] || 'prefer',
    )
        .trim()
        .toLowerCase()
    if (raw === 'must' || raw === 'none') return raw
    return 'prefer'
}

export function lifestyleFieldValue(
    lease: OccupancyLease,
    fieldId: LifestyleFieldId,
) {
    if (!lease.lifestyle_complete) return 'No lifestyle'
    const raw = lease.lifestyle_answers?.[fieldId]
    if (raw == null || raw === '') return '—'
    return SHORT_VALUES[raw] || raw
}

function pairHardFail(
    left: OccupancyLease,
    right: OccupancyLease,
    fieldId: LifestyleFieldId,
) {
    const dim = dimensionScore(
        fieldId,
        optionIndex(fieldId, left.lifestyle_answers?.[fieldId]),
        optionIndex(fieldId, right.lifestyle_answers?.[fieldId]),
    )
    if (dim !== 0) return false
    return (
        lifestyleImportance(left, fieldId) === 'must' ||
        lifestyleImportance(right, fieldId) === 'must'
    )
}

export function fieldConflictsInUnit(
    lease: OccupancyLease,
    occupants: Array<OccupancyLease | null>,
    fieldId: LifestyleFieldId,
) {
    if (!lease.lifestyle_complete) return false
    return occupants.some((other) => {
        if (!other || other.id === lease.id || !other.lifestyle_complete) {
            return false
        }
        return pairHardFail(lease, other, fieldId)
    })
}

export function petGroupConflict(occupants: Array<OccupancyLease | null>) {
    const people = occupants.filter((row): row is OccupancyLease => row != null)
    const petOwners = people.filter((row) => row.occupancy_facts?.has_pet)
    if (!petOwners.length) return false
    return people.some((row) => {
        if (row.occupancy_facts?.comfortable_with_roommate_pets !== false) {
            return false
        }
        if (row.occupancy_facts?.has_pet && petOwners.length === 1) {
            return false
        }
        return true
    })
}

export function petCellLabel(lease: OccupancyLease) {
    if (!lease.lifestyle_complete && lease.occupancy_facts == null) {
        return 'No lifestyle'
    }
    const facts = lease.occupancy_facts
    if (facts?.has_pet) return 'Has pet'
    if (facts?.comfortable_with_roommate_pets === false)
        return 'No roommate pets'
    if (facts?.comfortable_with_roommate_pets === true) return 'Pets OK'
    return '—'
}

export function genderCellLabel(lease: OccupancyLease) {
    return genderDisplayLabel(
        lease.occupancy_facts?.gender ?? lease.lifestyle_answers?.gender,
    )
}

export function genderGroupConflict(occupants: Array<OccupancyLease | null>) {
    const people = occupants.filter((row): row is OccupancyLease => row != null)
    if (!people.length) return false
    if (people.some((row) => row.occupancy_facts?.allow_coed_rooms)) {
        return false
    }
    const genders = people.map((row) =>
        normalizeGenderValue(
            row.occupancy_facts?.gender ?? row.lifestyle_answers?.gender,
        ),
    )
    if (
        genders.some(
            (value) => value === 'nonbinary' || value === 'prefer_not_to_say',
        )
    ) {
        return people.length > 1
    }
    return genders.includes('male') && genders.includes('female')
}

export function genderCellConflicts(
    lease: OccupancyLease,
    occupants: Array<OccupancyLease | null>,
) {
    if (!genderGroupConflict(occupants)) return false
    const value = normalizeGenderValue(
        lease.occupancy_facts?.gender ?? lease.lifestyle_answers?.gender,
    )
    return (
        value === 'male' ||
        value === 'female' ||
        value === 'nonbinary' ||
        value === 'prefer_not_to_say'
    )
}

export function petCellConflicts(
    lease: OccupancyLease,
    occupants: Array<OccupancyLease | null>,
) {
    if (!petGroupConflict(occupants)) return false
    const facts = lease.occupancy_facts
    return Boolean(
        facts?.has_pet || facts?.comfortable_with_roommate_pets === false,
    )
}

export type FlexibilityTag = 'flexible' | 'must_haves' | null

export function flexibilityTag(lease: OccupancyLease): FlexibilityTag {
    if (!lease.lifestyle_complete) return null
    const importances = LIFESTYLE_COLUMNS.map((column) =>
        lifestyleImportance(lease, column.id),
    )
    const musts = importances.filter((value) => value === 'must').length
    const nones = importances.filter((value) => value === 'none').length
    if (musts >= 2) return 'must_haves'
    if (nones >= 3 && musts === 0) return 'flexible'
    return null
}

export function lifestyleHoverText(lease: OccupancyLease) {
    if (!lease.lifestyle_complete) return 'Lifestyle incomplete'
    return LIFESTYLE_COLUMNS.map((column) => {
        const importance = lifestyleImportance(lease, column.id)
        const value = lifestyleFieldValue(lease, column.id)
        const label =
            importance === 'must'
                ? 'Must'
                : importance === 'none'
                ? 'No preference'
                : 'Prefer'
        return `${column.label}: ${value} · ${label}`
    }).join('\n')
}
